/**
 * @name useProcEqptLogic.js
 * @author 김권영
 * @since 2025. 02. 28.
 * @description
 * 사업장별 공정, 설비 및 공정-설비 매핑 관리 로직을 담당하는 React 훅
 * 관리자와 일반사용자 모두 siteCode 기반으로 동작
 *
 * @param {string} siteCode - 선택된 사업장 코드
 * @param {object} auth - 사용자 인증 정보
 * @returns {Object} - 함수와 상태를 반환
 */
import { useState, useEffect, useCallback } from "react";
import { get, post, put, del } from "@utils/axios_api";
import { toast } from 'react-toastify';

export const useProcEqptLogic = (siteCode, auth) => {
  // 공정 / 설비 목록
  const [procList, setProcList] = useState([]);
  const [eqptList, setEqptList] = useState([]);

  // 선택된 공정 / 가운데 공정별 설비 매핑
  const [selectedProc, setSelectedProc] = useState(null);
  const [centerProcEqptList, setCenterProcEqptList] = useState([]);

  // 로컬 상태 (공정 CRUD)
  const [editingProcId, setEditingProcId] = useState(null);
  const [editingProcName, setEditingProcName] = useState("");

  // 로컬 상태 (설비 CRUD)
  const [editingEqptId, setEditingEqptId] = useState(null);
  const [editingEqptName, setEditingEqptName] = useState("");

  useEffect(() => {
    setSelectedProc(null);
    setCenterProcEqptList([]);

    // 사업장이 변경되면 목록 초기화
    setProcList([]);
    setEqptList([]);
  }, [siteCode]);

  /**
   * 가운데 테이블(공정별 설비 매핑)을 조회하는 함수.
   * @param {Object} params - { siteCode, procCode }
   */
  const fetchCenterProcEqptList = useCallback(async (params) => {
    if (!params.siteCode || !params.procCode) {
      setCenterProcEqptList([]);
      return;
    }

    try {
      const data = await get("/user/procEqptMapping", {
        params: {
          siteCode: params.siteCode,
          procCode: params.procCode,
        },
      });
      setCenterProcEqptList(data.procEqptMappingList || []);
    } catch (err) {
      console.error("가운데 테이블 조회 오류:", err);
      setCenterProcEqptList([]);
    }
  }, []);

  /**
   * 공정 테이블과 설비 테이블을 조회하는 함수.
   * @param {Object} params - { siteCode }
   */
  const fetchProcEqptList = useCallback(async (params) => {
    if (!params.siteCode) {
      setProcList([]);
      setEqptList([]);
      return;
    }

    try {
      const data = await get("/user/procEqptList", {
        params: { siteCode: params.siteCode },
      });

      setProcList(data.procList || []);
      setEqptList(data.eqptList || []);

    } catch (err) {
      console.error("공정/설비 목록 조회 오류:", err);
      setProcList([]);
      setEqptList([]);
    }
  }, []);

  /**
   * 공정별 설비 매핑 목록을 저장 하는 함수.
   * @param {Object} params - { siteCode, procCode }
   */
  const handleSaveAll = useCallback(async (params) => {
    if (!params.siteCode) {
      toast.warn("사업장을 선택하세요.");
      return;
    }
    if (!params.procCode) {
      toast.warn("공정을 먼저 선택하세요.");
      return;
    }
    if (!window.confirm("저장 하시겠습니까?")) return;

    try {
      const payloadProcEqpt = centerProcEqptList.map((r) => ({
        mpreqCode: r.mpreqCode ?? 0,
        procCode: params.procCode,
        siteCode: params.siteCode,
        eqptCode: r.eqptCode
      }));

      const response = await post("/user/procEqptSave", {
        payloadProcEqpt,
        siteCode: params.siteCode,
        procCode: params.procCode,
      });

      toast.success(response.message);
      // 저장 후 가운데 데이터 재조회
      await fetchCenterProcEqptList(params);
    } catch (err) {
      console.error("저장 오류:", err);
    }
  }, [centerProcEqptList, fetchCenterProcEqptList]);

  function handleCenterRowClick(eqptCode) {
    setCenterProcEqptList((prev) =>
        prev.map((r) =>
            r.eqptCode === eqptCode ? { ...r, isChecked: !r.isChecked } : r
        )
    );
  }

  function handleRightRowClick(eqptCode) {
    setEqptList((prev) =>
        prev.map((r) =>
            r.eqptCode === eqptCode ? { ...r, isChecked: !r.isChecked } : r
        )
    );
  }

  // 설비 목록의 설비를 가운데 테이블에 추가 하는 함수 (실제 저장 X )
  const moveRightToCenter = () => {
    if (!selectedProc) {
      toast.warn("공정을 먼저 선택하세요.");
      return;
    }

    // 오른쪽 테이블에서 체크된 설비 선택
    const selectedEquipments = eqptList.filter(item => item.isChecked);

    if (selectedEquipments.length === 0) {
      toast.warn("이동할 설비를 선택하세요.");
      return;
    }

    const selectedProcess = procList.find(item => item.procCode === selectedProc);

    const newMappings = selectedEquipments
        .filter(eqpt => !centerProcEqptList.some(mapping => mapping.eqptCode === eqpt.eqptCode))
        .map(eqpt => ({
          eqptCode: eqpt.eqptCode,
          eqptName: eqpt.eqptName,
          procCode: selectedProc,
          procName: selectedProcess ? selectedProcess.procName : "",
          isChecked: false
        }));

    setCenterProcEqptList([...centerProcEqptList, ...newMappings]);

    // 선택된 설비들의 체크 해제
    setEqptList(prev =>
        prev.map(item => ({...item, isChecked: false}))
    );
  };

  // 가운데 테이블에서 체크된 설비 매핑 항목 삭제하는 메소드
  const moveCenterToRight = () => {
    const checkedItems = centerProcEqptList.filter(item => item.isChecked);

    if (checkedItems.length === 0) {
      toast.warn("제거할 설비를 선택하세요.");
      return;
    }

    setCenterProcEqptList(centerProcEqptList.filter(item => !item.isChecked));
  };

  // 공정 추가: 새 행을 추가 후 인라인 입력 시작
  const handleAddProcess = (params) => {
    if (!params.siteCode) {
      toast.warn("사업장을 선택하세요.");
      return;
    }

    const tempId = "new_" + Date.now(); // 임시 PK
    const newProcess = { procCode: tempId, procName: "", isNew: true };
    setProcList([...procList, newProcess]);
    setEditingProcId(tempId);
    setEditingProcName("");
  };

  // 공정 행 더블 클릭 시 수정 모드 전환
  const handleEditProcess = (item) => {
    setEditingProcId(item.procCode);
    setEditingProcName(item.procName);
  };

  // 공정 수정/추가 저장 (인풋 블러 또는 엔터)
  const saveProcessEdit = async (item, params) => {
    if (editingProcName.trim() === "") {
      toast.warn("공정명을 입력하세요.");
      return;
    }

    if (item.isNew) {
      // 신규 추가
      try {
        await post("/user/procAdd", {
          siteCode: params.siteCode,
          procName: editingProcName,
        });

        await fetchProcEqptList(params); // 공정 설비 재조회
        setEditingProcId(null);
        setEditingProcName("");
      } catch (err) {
        console.error("추가 오류:", err);
      }
    } else {
      // 기존 수정
      try {
        await put("/user/procUpdate", {
          procCode: item.procCode,
          procName: editingProcName,
        });

        setProcList(
            procList.map((p) =>
                p.procCode === item.procCode ? { ...p, procName: editingProcName } : p
            )
        );

        setCenterProcEqptList(
            centerProcEqptList.map((mapping) =>
                mapping.procCode === item.procCode
                    ? { ...mapping, procName: editingProcName }
                    : mapping
            )
        );

        setEditingProcId(null);
        setEditingProcName("");
      } catch (err) {
        console.error("수정 오류:", err);
      }
    }
  };

  // 선택된 공정 삭제
  const handleDeleteProcess = async (params) => {
    if (!selectedProc) {
      toast.warn("삭제할 공정을 선택하세요.");
      return;
    }

    if (window.confirm("해당 공정을 삭제하시겠습니까?\n연관된 매핑 데이터도 함께 삭제됩니다.")) {
      try {
        await del("/user/procDelete", {
          data: {
            procCode: selectedProc,
            siteCode: params.siteCode,
          }
        });

        setCenterProcEqptList([]);
        setSelectedProc(null);
        await fetchProcEqptList(params);
      } catch (err) {
        console.error("삭제 오류:", err);
      }
    }
  };

  // 설비 추가: 설비 목록 하단에 새 행 추가 후 인라인 입력 시작
  const handleAddEqpt = (params) => {
    if (!params.siteCode) {
      toast.warn("사업장을 선택하세요.");
      return;
    }

    const tempId = "new_" + Date.now();
    const newEqpt = { eqptCode: tempId, eqptName: "", isNew: true };
    setEqptList([...eqptList, newEqpt]);
    setEditingEqptId(tempId);
    setEditingEqptName("");
  };

  // 설비 행 더블 클릭 시 수정 모드 전환
  const handleEditEqpt = (item) => {
    setEditingEqptId(item.eqptCode);
    setEditingEqptName(item.eqptName);
  };

  // 설비 수정/추가 저장
  const saveEqptEdit = async (item, params) => {
    if (editingEqptName.trim() === "") {
      toast.warn("설비명을 입력하세요.");
      return;
    }

    if (item.isNew) {
      try {
        await post("/user/eqptAdd", {
          siteCode: params.siteCode,
          eqptName: editingEqptName,
        });

        await fetchProcEqptList(params); // 공정 설비 재조회
        setEditingEqptId(null);
        setEditingEqptName("");
      } catch (err) {
        console.error("추가 오류:", err);
      }
    } else {
      try {
        await put("/user/eqptUpdate", {
          eqptCode: item.eqptCode,
          eqptName: editingEqptName,
        });

        setEqptList(
            eqptList.map((e) =>
                e.eqptCode === item.eqptCode ? { ...e, eqptName: editingEqptName } : e
            )
        );

        setCenterProcEqptList(
            centerProcEqptList.map((mapping) =>
                mapping.eqptCode === item.eqptCode
                    ? { ...mapping, eqptName: editingEqptName }
                    : mapping
            )
        );

        setEditingEqptId(null);
        setEditingEqptName("");
      } catch (err) {
        console.error("수정 오류:", err);
      }
    }
  };

  // 선택된 설비 삭제
  const handleDeleteEqpt = async (params) => {
    const selectedEquipments = eqptList.filter((e) => e.isChecked);

    if (selectedEquipments.length === 0) {
      toast.warn("삭제할 설비를 선택하세요.");
      return;
    }

    if (selectedEquipments.length > 1) {
      toast.warn("한 번에 하나의 설비만 삭제할 수 있습니다. 하나만 선택해 주세요.");
      return;
    }

    const equipmentToDelete = selectedEquipments[0];

    if (window.confirm("해당 설비를 삭제하시겠습니까?\n연관된 매핑 데이터도 함께 삭제됩니다.")) {
      try {
        await del("/user/eqptDelete", {
          data: {
            eqptCode: equipmentToDelete.eqptCode,
            siteCode: params.siteCode,
          }
        });

        // 가운데 테이블에서 삭제된 설비 제거
        setCenterProcEqptList(centerProcEqptList.filter((e) => e.eqptCode !== equipmentToDelete.eqptCode));
        await fetchProcEqptList(params);
      } catch (err) {
        console.error("삭제 오류:", err);
      }
    }
  };

  return {
    procList, eqptList, centerProcEqptList,
    selectedProc, setSelectedProc, setCenterProcEqptList,
    fetchCenterProcEqptList, fetchProcEqptList,
    moveRightToCenter, moveCenterToRight,
    handleSaveAll, handleCenterRowClick, handleRightRowClick,
    editingProcId, editingProcName, setEditingProcName,
    editingEqptId, editingEqptName, setEditingEqptName,
    handleAddProcess, handleEditProcess, saveProcessEdit, handleDeleteProcess,
    handleAddEqpt, handleEditEqpt, saveEqptEdit, handleDeleteEqpt
  };
};